import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from '@mui/material'
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Loader } from '@/components/Loader/Loader'
import { ROUTES } from '@/constants'
import { useAppContext, useAppDispatch, useAppStore } from '@/hooks'
import { ResizableSidebarLayout } from '@/pages/User/components/ResizableSidebarLayout'
import { useSelectedBranch } from '@/pages/User/pages/Home/components/TreePanel/TreePanelContext'
import { getMergeRequestPath } from '@/pages/User/pages/MergeRequests/mrHelpers'
import { updateBranchTree } from '@/store/modules/settings/actions'

import { BRANCH_FILTER_STATUSES } from './branchFilterUi'
import {
	branchPathExpandedIds,
	BranchTreeSession,
	mergeExpandedIds,
	OPENED_BRANCH_STATUSES,
} from './branchTree'
import {
	BranchContextMenu,
	BranchMenuAnchor,
} from './components/BranchContextMenu'
import { BranchDetail } from './components/BranchDetail'
import { BranchesSidebar } from './components/BranchesSidebar'
import { CompareBranchDialog } from './components/CompareBranchDialog'
import { CreateBranchDialog } from './components/CreateBranchDialog'
import { EditBranchDialog } from './components/EditBranchDialog'
import { RebaseBranchDialog } from './components/RebaseBranchDialog'
import { useBranchActions } from './hooks/useBranchActions'
import { useBranches } from './hooks/useBranches'
import { pickDefaultBranchId } from './pickDefaultBranch'
import { BranchRecord, BranchStatus, getBranchesPath } from './types'

type BranchesContextValue = {
	branches: BranchRecord[]
}

const BranchesContext = createContext<BranchesContextValue | null>(null)

export const useBranchesContext = () => {
	const context = useContext(BranchesContext)
	if (!context) {
		throw new Error('useBranchesContext must be used within BranchesLayout')
	}
	return context
}

export const BranchesLayout = () => {
	const { t } = useAppContext()
	const navigate = useNavigate()
	const { '*': branchPath } = useParams<{ '*': string }>()
	const branchId = branchPath ? decodeURIComponent(branchPath) : undefined
	const dispatch = useAppDispatch()
	const { branches, hasSystemId, isError, isLoading, systemId } = useBranches({
		filter: { protectedOnly: false, statuses: BRANCH_FILTER_STATUSES },
	})
	const { selectedBranchId, setSelectedBranchId } = useSelectedBranch()
	const actions = useBranchActions()
	const [search, setSearch] = useState('')
	const branchTreeBySystem = useAppStore(
		(state) => state.settings.branchTreeBySystem,
	)
	const storedTree =
		systemId != null ? branchTreeBySystem[String(systemId)] : undefined
	const filter = storedTree?.customized
		? { protectedOnly: storedTree.protectedOnly, statuses: storedTree.statuses }
		: { protectedOnly: false, statuses: OPENED_BRANCH_STATUSES }
	const [expansionSystemId, setExpansionSystemId] = useState<number | null>(
		null,
	)
	const [userExpandedIds, setUserExpandedIds] = useState<string[]>([])
	const openedSystem = useRef<number | null>(null)

	const pathExpandedIds = useMemo(
		() => branchPathExpandedIds(branches, selectedBranchId),
		[branches, selectedBranchId],
	)
	const expandedIds =
		expansionSystemId === systemId
			? userExpandedIds
			: mergeExpandedIds(
					storedTree?.customized ? storedTree.expandedIds : [],
					pathExpandedIds,
				)

	const persistTree = useCallback(
		(session: BranchTreeSession) => {
			if (systemId == null) return
			dispatch(updateBranchTree(String(systemId), session))
		},
		[dispatch, systemId],
	)

	const handleToggleStatus = useCallback(
		(status: BranchStatus) => {
			const statuses = filter.statuses.includes(status)
				? filter.statuses.filter((value) => value !== status)
				: [...filter.statuses, status]
			persistTree({
				customized: true,
				expandedIds,
				protectedOnly: filter.protectedOnly,
				statuses,
			})
		},
		[expandedIds, filter.protectedOnly, filter.statuses, persistTree],
	)

	const handleToggleProtected = useCallback(() => {
		persistTree({
			customized: true,
			expandedIds,
			protectedOnly: !filter.protectedOnly,
			statuses: filter.statuses,
		})
	}, [expandedIds, filter.protectedOnly, filter.statuses, persistTree])

	const handleToggleExpanded = useCallback(
		(id: string) => {
			const next = expandedIds.includes(id)
				? expandedIds.filter((value) => value !== id)
				: [...expandedIds, id]
			setExpansionSystemId(systemId ?? null)
			setUserExpandedIds(next)
			persistTree({
				customized: true,
				expandedIds: next,
				protectedOnly: filter.protectedOnly,
				statuses: filter.statuses,
			})
		},
		[expandedIds, filter.protectedOnly, filter.statuses, persistTree, systemId],
	)
	const [isCreateOpen, setCreateOpen] = useState(false)
	const [createSourceId, setCreateSourceId] = useState<string | null>(null)
	const [menu, setMenu] = useState<BranchMenuAnchor | null>(null)
	const [comparePicker, setComparePicker] = useState<BranchRecord | null>(null)
	const [compareRequest, setCompareRequest] = useState<{
		branchId: string
		targetId: string
	} | null>(null)
	const [pendingDelete, setPendingDelete] = useState<BranchRecord | null>(null)
	const [editBranch, setEditBranch] = useState<BranchRecord | null>(null)
	const [rebaseTarget, setRebaseTarget] = useState<BranchRecord | null>(null)

	const selectedBranch =
		branches.find(
			(branch) => branch.id === branchId || branch.name === branchId,
		) ?? null
	const viewedBranchId = selectedBranch?.id ?? null
	const previousBranchId = useRef(viewedBranchId)

	useEffect(() => {
		if (previousBranchId.current === viewedBranchId) return
		previousBranchId.current = viewedBranchId
		setCompareRequest((current) =>
			current?.branchId === viewedBranchId ? current : null,
		)
	}, [viewedBranchId])

	useEffect(() => {
		if (isLoading || systemId == null || branches.length === 0) return
		if (openedSystem.current === systemId) return
		openedSystem.current = systemId
		const working =
			selectedBranchId &&
			branches.some((branch) => branch.id === selectedBranchId)
				? selectedBranchId
				: null
		const nextId = working ?? pickDefaultBranchId(branches, null)
		if (nextId && nextId !== branchId) {
			navigate(getBranchesPath(nextId), { replace: true })
		}
	}, [branchId, branches, isLoading, navigate, selectedBranchId, systemId])

	const handleSelectBranch = useCallback(
		(id: string) => {
			navigate(getBranchesPath(id))
		},
		[navigate],
	)

	const handleOpenMenu = useCallback(
		(anchor: HTMLElement, branch: BranchRecord) => {
			setMenu({ anchor, branch })
		},
		[],
	)

	const handleDeleted = useCallback(
		(deleted: BranchRecord) => {
			const remaining = branches.filter((branch) => branch.id !== deleted.id)
			const nextId = pickDefaultBranchId(remaining, selectedBranchId)
			if (nextId) {
				navigate(getBranchesPath(nextId), { replace: true })
			} else {
				navigate(ROUTES.BRANCHES, { replace: true })
			}
		},
		[branches, navigate, selectedBranchId],
	)

	const value = useMemo(() => ({ branches }), [branches])

	return (
		<BranchesContext.Provider value={value}>
			<Box
				data-testid="branches-page"
				sx={{
					display: 'flex',
					height: '100%',
					minHeight: 0,
					overflow: 'hidden',
					position: 'relative',
				}}
			>
				<Loader loaded={!isLoading} $absolute />
				<ResizableSidebarLayout
					defaultWidth={300}
					localStorageKey="mdd-sidebar-width-branches"
					content={
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								height: '100%',
								minWidth: 0,
								minHeight: 0,
							}}
						>
							{!hasSystemId ? (
								<EmptyState message={t('BRANCHES_NO_SYSTEM_HINT')} />
							) : isError ? (
								<EmptyState message={t('BRANCHES_LOAD_FAILED')} />
							) : (
								<BranchDetail
									activeBranchId={selectedBranchId}
									branch={selectedBranch}
									branches={branches}
									compareTargetId={
										compareRequest &&
										compareRequest.branchId === selectedBranch?.id
											? compareRequest.targetId
											: null
									}
									onCreateMerge={() => {
										if (!selectedBranch) return
										navigate(getMergeRequestPath(), {
											state: {
												createMergeSourceBranchId: selectedBranch.id,
												createMergeTargetBranchId:
													selectedBranch.sourceBranchId,
											},
										})
									}}
									onOpenMenu={handleOpenMenu}
									onUpdateDeploymentPackage={() => {
										if (selectedBranch) {
											void actions.refreshDeploymentPackage(selectedBranch)
										}
									}}
									updatingDeploymentPackage={
										actions.updatingDeploymentPackageId === selectedBranch?.id
									}
								/>
							)}
						</Box>
					}
				>
					<BranchesSidebar
						activeBranchId={selectedBranchId}
						branchId={selectedBranch?.id ?? null}
						branches={branches}
						canCreate={hasSystemId}
						expandedIds={expandedIds}
						filter={filter}
						search={search}
						setSearch={setSearch}
						onOpenCreate={() => {
							setCreateSourceId(null)
							setCreateOpen(true)
						}}
						onOpenMenu={handleOpenMenu}
						onSelectBranch={handleSelectBranch}
						onToggleExpanded={handleToggleExpanded}
						onToggleProtected={handleToggleProtected}
						onToggleStatus={handleToggleStatus}
					/>
				</ResizableSidebarLayout>
			</Box>
			<CreateBranchDialog
				isOpen={isCreateOpen}
				initialSourceId={createSourceId}
				systemId={systemId}
				branches={branches}
				onClose={() => {
					setCreateOpen(false)
					setCreateSourceId(null)
				}}
				onCreated={(id) => navigate(getBranchesPath(id))}
			/>
			<EditBranchDialog
				branch={editBranch}
				branches={branches}
				isOpen={editBranch != null}
				onClose={() => setEditBranch(null)}
				systemId={systemId}
			/>
			<RebaseBranchDialog
				branch={rebaseTarget}
				isOpen={rebaseTarget != null}
				onClose={() => setRebaseTarget(null)}
			/>
			<BranchContextMenu
				activeBranchId={selectedBranchId}
				canManage={actions.canManage}
				ctx={menu}
				onClose={() => setMenu(null)}
				onCompare={(branch) => setComparePicker(branch)}
				onCreate={(branch) => {
					setCreateSourceId(branch.id)
					setCreateOpen(true)
				}}
				onCreateMerge={(branch) => {
					navigate(getMergeRequestPath(), {
						state: {
							createMergeSourceBranchId: branch.id,
							createMergeTargetBranchId: branch.sourceBranchId,
						},
					})
				}}
				onDelete={(branch) => setPendingDelete(branch)}
				onEditDefinition={setEditBranch}
				onRebase={setRebaseTarget}
				onSetActive={setSelectedBranchId}
				onSetStatus={actions.setStatus}
				onToggleProtected={actions.toggleProtected}
			/>
			{comparePicker ? (
				<CompareBranchDialog
					branch={comparePicker}
					branches={branches}
					onClose={() => setComparePicker(null)}
					onConfirm={(target) => {
						const source = comparePicker
						setCompareRequest({ branchId: source.id, targetId: target.id })
						setComparePicker(null)
						if (source.id !== selectedBranch?.id) {
							navigate(getBranchesPath(source.id))
						}
					}}
				/>
			) : null}
			<Dialog
				open={Boolean(pendingDelete)}
				onClose={() => setPendingDelete(null)}
				maxWidth="xs"
				fullWidth
				slotProps={{ paper: { sx: { borderRadius: '14px' } } }}
			>
				<DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: '#1F4954' }}>
					{t('DELETE')}
				</DialogTitle>
				<DialogContent>
					<Typography sx={{ fontSize: 13, color: '#3D3D3D' }}>
						{t('BRANCHES_DELETE_CONFIRM', [pendingDelete?.name ?? ''])}
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button
						onClick={() => setPendingDelete(null)}
						sx={{ textTransform: 'none' }}
					>
						{t('CANCEL')}
					</Button>
					<Button
						color="error"
						variant="contained"
						disableElevation
						onClick={() => {
							const target = pendingDelete
							setPendingDelete(null)
							if (!target) return
							void actions.removeBranch(target).then((ok) => {
								if (ok) handleDeleted(target)
							})
						}}
						sx={{ textTransform: 'none' }}
						data-testid="branch-delete-confirm"
					>
						{t('DELETE')}
					</Button>
				</DialogActions>
			</Dialog>
		</BranchesContext.Provider>
	)
}

const EmptyState = ({ message }: { message: string }) => (
	<Box
		sx={{
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			height: '100%',
			color: '#A8B4B8',
			px: 3,
		}}
	>
		<Typography sx={{ fontSize: 13 }}>{message}</Typography>
	</Box>
)

export const BranchesEmbedded = () => (
	<Box sx={{ height: '100%', minHeight: 0 }}>
		<BranchesLayout />
	</Box>
)

export const getBranchPathOrRoot = (branchId?: string) =>
	branchId ? getBranchesPath(branchId) : ROUTES.BRANCHES
